import React, { useCallback, useState } from "react";
import { Stack, Alert, Text } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import { useNavigate } from "react-router-dom";

import { useEvents } from "@/hooks";
import {
  EventImportInput,
  ImportedEventInput,
  EventImportStats,
} from "@/utils/api";

import { DashboardLayout } from "@/components/layouts";
import { AppButton, AppIcon } from "@/components/generic";
import { ParsedEvent, EventImportWizard } from "@/components/features";

export const EventImportPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();

  // Use the events hook for API operations
  const {
    import: importEvents,
    isImporting,
    lastError,
    clearError,
  } = useEvents({
    autoLoad: false,
  });

  // Local state for wizard management
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [importSuccessStats, setImportSuccessStats] =
    useState<EventImportStats | null>(null);

  // Convert ParsedEvent to ImportedEventInput for our API
  const convertParsedEventToImportInput = useCallback(
    (parsedEvent: ParsedEvent): ImportedEventInput => {
      return {
        eventUid: parsedEvent.uid,
        name: parsedEvent.title || "Untitled Event",
        description: parsedEvent.description || undefined,
        eventDate: parsedEvent.startDateTime
          ? new Date(parsedEvent.startDateTime).toISOString().split("T")[0]
          : undefined,
        startTime:
          parsedEvent.startDateTime && !parsedEvent.isAllDay
            ? new Date(parsedEvent.startDateTime).toTimeString().split(" ")[0]
            : undefined,
        endTime:
          parsedEvent.endDateTime && !parsedEvent.isAllDay
            ? new Date(parsedEvent.endDateTime).toTimeString().split(" ")[0]
            : undefined,
        organizer: parsedEvent.organizer,
        status: parsedEvent.status,
      };
    },
    [],
  );

  // Handle bulk events import - this is the main import function
  const handleEventsImport = useCallback(
    async (
      parsedEvents: ParsedEvent[],
      venueId: string,
      importMode: "merge" | "replace",
    ): Promise<EventImportStats | null> => {
      try {
        setWizardError(null);
        clearError();

        // Convert all parsed events to import format
        const importedEvents = parsedEvents.map(
          convertParsedEventToImportInput,
        );

        const importData: EventImportInput = {
          mode: importMode,
          venueId: venueId,
          events: importedEvents,
        };

        const result = await importEvents(importData);

        if (result) {
          return result;
        } else {
          setWizardError(lastError || "Failed to import events");
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        setWizardError(errorMessage);
        return null;
      }
    },
    [importEvents, convertParsedEventToImportInput, clearError, lastError],
  );

  // Handle wizard completion
  const handleWizardComplete = useCallback(
    (stats: EventImportStats) => {
      setImportSuccessStats(stats);
      setWizardError(null);
      clearError();
    },
    [clearError],
  );

  // Handle wizard cancellation
  const handleWizardCancel = useCallback(() => {
    navigate("/events");
  }, [navigate]);

  // Clear success message and start over
  const handleStartOver = useCallback(() => {
    setImportSuccessStats(null);
    setWizardError(null);
    clearError();
  }, [clearError]);

  // Calculate total imported events for display
  const totalImported = importSuccessStats
    ? importSuccessStats.inserted + importSuccessStats.updated
    : 0;

  return (
    <DashboardLayout {...props}>
      <Stack gap="lg">
        {/* Page header */}
        <Stack gap="xs">
          <Text size="xl" fw={700}>
            Import Calendar Events
          </Text>
          <Text size="sm" c="dimmed">
            Upload an iCalendar (.ics) file to import events into your calendar
          </Text>
        </Stack>

        {/* Success message */}
        {importSuccessStats !== null && (
          <Alert
            color="green"
            icon={<AppIcon iconName="CircleCheck" size={16} />}
            onClose={() => setImportSuccessStats(null)}
            withCloseButton
          >
            <Stack gap="xs">
              <Text fw={500}>Import Completed Successfully!</Text>
              <Text size="sm">
                {totalImported > 0 ? (
                  <>
                    Successfully imported {totalImported} event
                    {totalImported !== 1 ? "s" : ""}.
                    {importSuccessStats.inserted > 0 && (
                      <>
                        {" "}
                        {importSuccessStats.inserted} new event
                        {importSuccessStats.inserted !== 1 ? "s" : ""}
                      </>
                    )}
                    {importSuccessStats.updated > 0 && (
                      <>
                        {importSuccessStats.inserted > 0 ? ", " : " "}
                        {importSuccessStats.updated} updated
                      </>
                    )}
                    .
                  </>
                ) : (
                  "No events were imported."
                )}
              </Text>
              <AppButton
                iconName="RotateCcw"
                size="sm"
                onClick={handleStartOver}
              >
                Import more events
              </AppButton>
            </Stack>
          </Alert>
        )}

        {/* Wizard error */}
        {wizardError && (
          <Alert
            color="red"
            icon={<AppIcon iconName="AlertCircle" size={16} />}
            onClose={() => setWizardError(null)}
            withCloseButton
          >
            <Text fw={500}>Import Error</Text>
            <Text size="sm">{wizardError}</Text>
          </Alert>
        )}

        {/* API error from useEvents hook */}
        {lastError && !wizardError && (
          <Alert
            color="red"
            icon={<AppIcon iconName="AlertCircle" size={16} />}
            onClose={clearError}
            withCloseButton
          >
            <Text fw={500}>API Error</Text>
            <Text size="sm">{lastError}</Text>
          </Alert>
        )}

        {/* Import wizard */}
        {importSuccessStats === null && (
          <EventImportWizard
            disabled={isImporting}
            maxFileSize={5 * 1024 * 1024} // 5MB
            listHeight={600}
            onEventsImport={handleEventsImport}
            onComplete={handleWizardComplete}
            onCancel={handleWizardCancel}
          />
        )}
      </Stack>
    </DashboardLayout>
  );
};
